import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { RecordingCard } from "@pages:app/audio/components/RecordingCard";
import { RecordingPlayer } from "@pages:app/audio/components/RecordingPlayer";
import {
  AudioPlayer,
  TranscriptionJob,
  useElapsedSeconds,
} from "@pages:app/audio/hooks";
import { formatClock, requestMeta } from "@pages:app/audio/util";
import { useTranslation } from "react-i18next";

interface ProcessingCardProps {
  job: TranscriptionJob;
  player: AudioPlayer;
}

/*
 * The recording while the job runs, with the time spent so far.
 * A long wait in the queue is a signal in itself, so the two stages are named.
 */
export const ProcessingCard = ({ job, player }: ProcessingCardProps) => {
  const { t } = useTranslation();
  const elapsed = useElapsedSeconds(job.startedAt);
  const label =
    job.phase === "processing"
      ? t("audio.processing.transcribing")
      : t("audio.processing.queued");

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      <RecordingCard
        file={job.file}
        meta={requestMeta(
          {
            ...job,
            durationSeconds: job.waveform?.durationSeconds,
          },
          t,
        )}
        top={<RecordingPlayer player={player} waveform={job.waveform} sweep />}
      >
        <CircularProgress size={22} thickness={5} />
        <Typography
          aria-live="polite"
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {label} {formatClock(elapsed)}
        </Typography>
      </RecordingCard>
      {job.error && (
        <Typography
          role="alert"
          sx={{ color: "error.main", fontSize: 14, textAlign: "center", mt: 2 }}
        >
          {job.error}
        </Typography>
      )}
      {/* The job cannot be cancelled on the server, TODO? */}
      <Box sx={{ textAlign: "center", mt: 2 }}>
        <Button variant="text" size="small" onClick={job.reset}>
          {t("audio.processing.startOver")}
        </Button>
      </Box>
    </Box>
  );
};
